import type {
  FieldValue,
  FileFieldValue,
  FormField
} from '@elfsight/widget-form-builder/widget';
import type { CustomFormField } from '../../../../types';
import { findInOptions } from '../../../form/utils';

const EXCLUDED_SUBMIT_FIELD_TYPES = ['label', 'heading', 'paragraph'];
const FIELDS_WITH_SELECT = ['choice', 'imageChoice', 'dropdown'];

function getSelectValue(field: CustomFormField, value: string | number) {
  const option = findInOptions(value, field.options);

  if (!option) {
    return value;
  }

  if (field.type === 'choice') {
    return option?.name || value;
  }

  if (field.type === 'imageChoice' && 'index' in option) {
    return option?.name || option?.index;
  }

  return option?.name;
}

function getFileValue(rawValue: FileFieldValue) {
  return rawValue.reduce<string[]>((acc, entry) => {
    if (entry.uploaded) {
      acc.push(entry.uploaded);
    }

    return acc;
  }, []);
}

function isFileFieldValue(fieldValue: unknown): fieldValue is FileFieldValue {
  return (
    Array.isArray(fieldValue) &&
    !fieldValue.find((value) => !(value instanceof File))
  );
}

export function getProcessedSubmitFields(
  derivedFields: Record<string, FormField | { value?: FieldValue }>,
  popupFields: CustomFormField[]
) {
  return popupFields.reduce<Record<string, Partial<CustomFormField>>>(
    (acc, calcField) => {
      const { fieldID, name, type, id } = calcField;
      let value = derivedFields[id].value;

      if (EXCLUDED_SUBMIT_FIELD_TYPES.includes(type)) {
        return acc;
      }

      if (isFileFieldValue(value)) {
        value = getFileValue(value);
      } else if (value && FIELDS_WITH_SELECT.includes(type)) {
        value = Array.isArray(value)
          ? value.map((item) => getSelectValue(calcField, item) as string)
          : getSelectValue(calcField, value);
      }

      acc[id] = { fieldID, name, type, value };

      return acc;
    },
    {}
  );
}
